import { addBreadcrumb, captureException } from "@/infrastructure/monitoring";
import { extractFilenameFromHeader } from "./fileDownload";
import { ApiClientError } from "@/application/errors/ApiClientError";
import { apiConfig } from "./config";
import {
	getLocalStorageService,
	getLogger,
	getMonitoring,
	getSessionExpiredNotifier,
	getSessionStorageService,
} from "@/infrastructure/di/services";
import { STORAGE_KEYS } from "@/infrastructure/storage/constants";
import type { ApiResponse } from "@/application/ports/types";
import { TERMINAL_AUTH_ERROR_CODES } from "@/domain/auth";

export type { ApiResponse } from "@/application/ports/types";

export { ApiClientError } from "@/application/errors/ApiClientError";
export type { ApiClientErrorData as ApiError } from "@/application/errors/ApiClientError";

// Sensitive fields to filter from request bodies before sending to Sentry
const SENSITIVE_FIELDS = [
	"password",
	"token",
	"access_token",
	"refresh_token",
	"secret",
	"api_key",
	"authorization",
	"credit_card",
	"card_number",
	"cvv",
	"ssn",
	"tax_id",
	"bank_account",
];

/**
 * Sanitize request body by removing sensitive fields.
 * Returns a safe version for logging/monitoring.
 */
function sanitizeRequestBody(
	body: unknown,
): Record<string, unknown> | string | null {
	if (!body) return null;

	// Handle string bodies (already serialized JSON)
	if (typeof body === "string") {
		try {
			const parsed = JSON.parse(body);
			return sanitizeRequestBody(parsed) as Record<string, unknown>;
		} catch {
			// Not JSON, might be form data or other format
			return "[non-JSON body]";
		}
	}

	// Handle FormData
	if (body instanceof FormData) {
		const sanitized: Record<string, string> = {};
		body.forEach((value, key) => {
			const lowerKey = key.toLowerCase();
			if (SENSITIVE_FIELDS.some((field) => lowerKey.includes(field))) {
				sanitized[key] = "[FILTERED]";
			} else if (value instanceof File) {
				sanitized[key] = `[File: ${value.name}, ${value.size} bytes]`;
			} else {
				sanitized[key] = String(value);
			}
		});
		return sanitized;
	}

	// Handle objects
	if (typeof body === "object" && body !== null) {
		const sanitized: Record<string, unknown> = {};
		for (const [key, value] of Object.entries(body)) {
			const lowerKey = key.toLowerCase();
			if (SENSITIVE_FIELDS.some((field) => lowerKey.includes(field))) {
				sanitized[key] = "[FILTERED]";
			} else if (typeof value === "object" && value !== null) {
				sanitized[key] = sanitizeRequestBody(value);
			} else {
				sanitized[key] = value;
			}
		}
		return sanitized;
	}

	return null;
}

/**
 * Extract query parameters from endpoint URL.
 */
function extractQueryParams(endpoint: string): Record<string, string> | null {
	const queryStart = endpoint.indexOf("?");
	if (queryStart === -1) return null;

	const params: Record<string, string> = {};
	const searchParams = new URLSearchParams(endpoint.slice(queryStart));
	searchParams.forEach((value, key) => {
		const lowerKey = key.toLowerCase();
		if (SENSITIVE_FIELDS.some((field) => lowerKey.includes(field))) {
			params[key] = "[FILTERED]";
		} else {
			params[key] = value;
		}
	});
	return Object.keys(params).length > 0 ? params : null;
}

class ApiClient {
	private baseUrl: string;
	private timeout: number;

	// Request deduplication: prevents duplicate concurrent requests to the same endpoint
	// Key: request signature (method + url + body hash), Value: pending promise
	private pendingRequests = new Map<string, Promise<unknown>>();
	private sessionExpiredHandled = false;

	// Request deduplication TTL in ms (how long to dedupe after a request completes)
	private static readonly DEDUP_TTL_MS = 100;

	// Maximum number of pending requests to track (prevents unbounded memory growth)
	private static readonly MAX_PENDING_REQUESTS = 100;

	private uploadTimeout: number;

	constructor() {
		this.baseUrl = apiConfig.baseUrl;
		this.timeout = apiConfig.timeout;
		this.uploadTimeout = apiConfig.uploadTimeout;
		// Don't initialize immediately, do it lazily when needed
	}

	/**
	 * Generate a unique key for request deduplication.
	 * Only GET requests are deduplicated (mutations should always execute).
	 */
	private getRequestKey(method: string, endpoint: string): string | null {
		// Only dedupe GET requests
		if (method !== "GET") return null;
		return `${method}:${endpoint}`;
	}

	private async requestWithResponseType<T>(
		endpoint: string,
		responseType: "json" | "blob" | "text" | "response" = "json",
		options: RequestInit & { timeout?: number } = {},
		retryCount = 0,
	): Promise<T | Blob | string | null> {
		const method = options.method || "GET";
		const requestKey = this.getRequestKey(method, endpoint);

		// Check for duplicate in-flight request (only for GET requests)
		if (requestKey && this.pendingRequests.has(requestKey)) {
			// Return the existing promise - this prevents duplicate HTTP requests
			return this.pendingRequests.get(requestKey) as Promise<T | Blob | string>;
		}

		// Create the actual request
		const requestPromise = this.executeRequest<T>(
			endpoint,
			responseType,
			options,
			retryCount,
		);

		// Store the promise for deduplication (only for GET requests)
		if (requestKey) {
			// Prevent unbounded memory growth - clear if we hit the limit
			if (this.pendingRequests.size >= ApiClient.MAX_PENDING_REQUESTS) {
				this.pendingRequests.clear();
			}

			this.pendingRequests.set(requestKey, requestPromise);

			// Remove from pending after completion (with small TTL to catch rapid-fire requests)
			requestPromise.finally(() => {
				setTimeout(() => {
					this.pendingRequests.delete(requestKey);
				}, ApiClient.DEDUP_TTL_MS);
			});
		}

		return requestPromise;
	}

	private async executeRequest<T>(
		endpoint: string,
		responseType: "json" | "blob" | "text" | "response" = "json",
		options: RequestInit & { timeout?: number } = {},
		retryCount = 0,
	): Promise<T | Blob | string | null> {
		const url = `${this.baseUrl}${endpoint}`;
		const controller = new AbortController();
		const method = options.method || "GET";
		const startTime = Date.now();

		// Add breadcrumb for request start
		addBreadcrumb({
			category: "api",
			message: `${method} ${endpoint}`,
			level: "info",
			data: {
				method,
				endpoint,
				hasBody: !!options.body,
			},
		});

		const defaultHeaders: Record<string, string> = {};

		// Only set Content-Type for non-FormData requests
		if (!(options.body instanceof FormData)) {
			defaultHeaders["Content-Type"] = "application/json";
		}

		// Only try to get token for non-auth endpoints
		if (!this.isAuthEndpoint(endpoint)) {
			try {
				const token = await this.getValidToken();
				if (token) {
					defaultHeaders["Authorization"] = `Bearer ${token}`;
				}
			} catch (error) {
				// If we can't get a token for non-auth endpoints, that's ok
				// The server will return 401 and we can handle it
				getLogger().debug("Could not get token for request:", error);
			}
		}

		// Add organization header if available
		const orgUuid = getLocalStorageService().get<string>(
			STORAGE_KEYS.CURRENT_ORG_UUID,
		);
		if (orgUuid) {
			defaultHeaders["X-Organization-UUID"] = orgUuid;
		}

		// Start timeout AFTER token resolution so token refresh doesn't eat into the budget
		const requestTimeout = options.timeout ?? this.timeout;
		const timeoutId = setTimeout(() => {
			controller.abort();
		}, requestTimeout);

		try {
			const response = await fetch(url, {
				...options,
				headers: {
					...defaultHeaders,
					...options.headers,
				},
				signal: controller.signal,
			});

			clearTimeout(timeoutId);

			// Handle error responses before processing body
			if (!response.ok) {
				let errorData;
				const contentType = response.headers.get("content-type");

				if (contentType && contentType.includes("application/json")) {
					errorData = await response.json();
				} else {
					errorData = await response.text();
				}

				// Handle 401 Unauthorized
				if (response.status === 401 && !this.isAuthEndpoint(endpoint)) {
					// Check if it's a terminal auth error (revoked, expired, or missing refresh token)
					if (TERMINAL_AUTH_ERROR_CODES.includes(errorData?.error)) {
						// This is a critical auth failure - user must re-login
						await this.handleRevokedRefreshToken();
						throw new ApiClientError({
							message: "Your session has expired. Please log in again.",
							status: 401,
							code: "REFRESH_TOKEN_REVOKED",
							details: errorData,
						});
					}

					// For other 401 errors, attempt token refresh once
					if (retryCount === 0) {
						const newToken = await this.refreshExpiredToken();
						if (newToken) {
							// Retry the request with the new token (bypass dedup for retries)
							return this.executeRequest<T>(
								endpoint,
								responseType,
								options,
								retryCount + 1,
							);
						}
						// Refresh failed — session is unrecoverable, trigger logout
						await this.handleRevokedRefreshToken();
						throw new ApiClientError({
							message: "Your session has expired. Please log in again.",
							status: 401,
							code: "SESSION_EXPIRED",
							details: errorData,
						});
					}
					// Retry already attempted, still 401 — force logout
					await this.handleRevokedRefreshToken();
					throw new ApiClientError({
						message: "Your session has expired. Please log in again.",
						status: 401,
						code: "SESSION_EXPIRED",
						details: errorData,
					});
				}

				// Add breadcrumb for failed request
				addBreadcrumb({
					category: "api",
					message: `${method} ${endpoint} - ${response.status} FAILED`,
					level: "error",
					data: {
						method,
						endpoint,
						status: response.status,
						errorCode: errorData?.code,
						duration: Date.now() - startTime,
					},
				});

				const apiError = new ApiClientError({
					message:
						errorData?.message ||
						`HTTP ${response.status}: ${response.statusText}`,
					status: response.status,
					code: errorData?.code || errorData?.error,
					details: errorData?.details,
					endpoint,
					method,
				});

				const shouldReport = response.status >= 500 || response.status === 404;

				if (shouldReport) {
					// Extract query params and sanitize body for debugging context
					const queryParams = extractQueryParams(endpoint);
					const sanitizedBody = sanitizeRequestBody(options.body);

					captureException(apiError, {
						fingerprint: [
							"api-error",
							endpoint.split("?")[0],
							String(response.status),
						],
						tags: {
							"api.endpoint": endpoint.split("?")[0], // Base endpoint without query
							"api.method": method,
							"api.status": String(response.status),
						},
						extra: {
							// Backend response (the "reason")
							errorResponse: errorData,
							// Request details
							request: {
								endpoint,
								method,
								queryParams,
								body: sanitizedBody,
							},
							// Timing
							duration: Date.now() - startTime,
							// Organization context
							organizationUuid: getLocalStorageService().get<string>(
								STORAGE_KEYS.CURRENT_ORG_UUID,
							),
						},
					});
				}

				throw apiError;
			}

			// Add breadcrumb for successful request
			addBreadcrumb({
				category: "api",
				message: `${method} ${endpoint} - ${response.status}`,
				level: "info",
				data: {
					method,
					endpoint,
					status: response.status,
					duration: Date.now() - startTime,
				},
			});

			// Process successful response based on type
			if (response.status === 204 && responseType !== "response") {
				// 204 No Content: return null, caller should type T as void or nullable
				return null;
			}

			switch (responseType) {
				case "response":
					return response as unknown as T;
				case "blob":
					return await response.blob();
				case "text":
					return await response.text();
				case "json":
				default:
					return await response.json();
			}
		} catch (error) {
			clearTimeout(timeoutId);

			if (error instanceof Error && error.name === "AbortError") {
				// Add breadcrumb for timeout
				addBreadcrumb({
					category: "api",
					message: `${method} ${endpoint} - TIMEOUT`,
					level: "error",
					data: {
						method,
						endpoint,
						duration: Date.now() - startTime,
					},
				});

				throw new ApiClientError({
					message: "Request timeout",
					status: 408,
					code: "TIMEOUT",
					endpoint,
					method,
				});
			}

			if (error instanceof ApiClientError) {
				throw error;
			}

			// Add breadcrumb for network error
			addBreadcrumb({
				category: "api",
				message: `${method} ${endpoint} - NETWORK_ERROR`,
				level: "error",
				data: {
					method,
					endpoint,
					originalError: error instanceof Error ? error.message : String(error),
					duration: Date.now() - startTime,
				},
			});

			// Network errors (fetch failed, DNS issues, etc.)
			const networkError = new ApiClientError({
				message: "Network error or unexpected response",
				status: 0,
				code: "NETWORK_ERROR",
				details: {
					originalError: error instanceof Error ? error.message : String(error),
				},
				endpoint,
				method,
			});

			// Report network errors to Sentry (could indicate backend issues)
			const queryParams = extractQueryParams(endpoint);
			const sanitizedBody = sanitizeRequestBody(options.body);

			captureException(networkError, {
				fingerprint: ["api-error", endpoint.split("?")[0], "NETWORK_ERROR"],
				tags: {
					"api.endpoint": endpoint.split("?")[0],
					"api.method": method,
					"api.status": "0",
				},
				extra: {
					originalError: error instanceof Error ? error.message : String(error),
					request: {
						endpoint,
						method,
						queryParams,
						body: sanitizedBody,
					},
					duration: Date.now() - startTime,
					organizationUuid: getLocalStorageService().get<string>(
						STORAGE_KEYS.CURRENT_ORG_UUID,
					),
				},
			});

			throw networkError;
		}
	}

	private async request<T>(
		endpoint: string,
		options: RequestInit & { timeout?: number } = {},
		retryCount = 0,
	): Promise<ApiResponse<T>> {
		const data = await this.requestWithResponseType<T>(
			endpoint,
			"json",
			options,
			retryCount,
		);
		const dataRecord =
			typeof data === "object" && data !== null
				? (data as Record<string, unknown>)
				: null;
		const message: string | undefined =
			typeof dataRecord?.message === "string" ? dataRecord.message : undefined;
		return {
			data: data as T,
			message,
			status: 200, // Will be overridden by actual status if needed
			success: true,
		};
	}

	async get<T>(
		endpoint: string,
		options?: RequestInit,
	): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, {
			...options,
			method: "GET",
		});
	}

	async getText(
		endpoint: string,
		options?: RequestInit & { timeout?: number },
	): Promise<string> {
		const result = await this.requestWithResponseType<string>(
			endpoint,
			"text",
			{
				...options,
				method: "GET",
			},
		);
		return String(result);
	}

	async post<T>(
		endpoint: string,
		body?: unknown,
		options?: RequestInit & { timeout?: number },
	): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, {
			...options,
			method: "POST",
			body:
				body instanceof FormData
					? body
					: body
						? JSON.stringify(body)
						: undefined,
		});
	}

	async put<T>(
		endpoint: string,
		body?: unknown,
		options?: RequestInit,
	): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, {
			...options,
			method: "PUT",
			body:
				body instanceof FormData
					? body
					: body
						? JSON.stringify(body)
						: undefined,
		});
	}

	async patch<T>(
		endpoint: string,
		body?: unknown,
		options?: RequestInit,
	): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, {
			...options,
			method: "PATCH",
			body:
				body instanceof FormData
					? body
					: body
						? JSON.stringify(body)
						: undefined,
		});
	}

	async delete<T>(
		endpoint: string,
		options?: RequestInit,
	): Promise<ApiResponse<T>> {
		return this.request<T>(endpoint, {
			...options,
			method: "DELETE",
		});
	}

	async upload<T>(
		endpoint: string,
		formData: FormData,
		options?: RequestInit & { timeout?: number },
	): Promise<ApiResponse<T>> {
		// Let request() handle the token management, just like other methods
		// Don't set Content-Type for FormData — let the browser handle the multipart boundary
		// Use uploadTimeout by default — file uploads need more time than regular API calls
		return this.request<T>(endpoint, {
			timeout: this.uploadTimeout,
			...options,
			method: "POST",
			body: formData,
		});
	}

	async getBlob(
		endpoint: string,
		options?: RequestInit & { timeout?: number },
	): Promise<Blob> {
		const result = await this.requestWithResponseType<Blob>(endpoint, "blob", {
			...options,
			method: "GET",
		});
		if (!(result instanceof Blob)) throw new Error("Expected Blob response");
		return result;
	}

	async getBlobWithFilename(
		endpoint: string,
		fallbackFilename: string,
		options?: RequestInit & { timeout?: number },
	): Promise<{ blob: Blob; filename: string }> {
		const response = await this.executeRequest<Response>(endpoint, "response", {
			...options,
			method: "GET",
		});
		const blob = await response.blob();
		const contentDisposition = response.headers.get("Content-Disposition");
		const filename = extractFilenameFromHeader(
			contentDisposition,
			fallbackFilename,
		);
		return { blob, filename };
	}

	async postBlob(
		endpoint: string,
		body: unknown,
		options?: RequestInit & { timeout?: number },
	): Promise<Blob> {
		const result = await this.requestWithResponseType<Blob>(endpoint, "blob", {
			...options,
			method: "POST",
			body: JSON.stringify(body),
		});
		if (!(result instanceof Blob)) throw new Error("Expected Blob response");
		return result;
	}

	async postBlobWithFilename(
		endpoint: string,
		body: unknown,
		fallbackFilename: string,
		options?: RequestInit & { timeout?: number },
	): Promise<{ blob: Blob; filename: string }> {
		const response = await this.executeRequest<Response>(endpoint, "response", {
			...options,
			method: "POST",
			body: JSON.stringify(body),
		});
		const blob = await response.blob();
		const contentDisposition = response.headers.get("Content-Disposition");
		const filename = extractFilenameFromHeader(
			contentDisposition,
			fallbackFilename,
		);
		return { blob, filename };
	}

	private async getValidToken(): Promise<string | null> {
		try {
			const { getTokenManager } = await import("@/infrastructure/di/services");
			return await getTokenManager().ensureValidToken();
		} catch {
			return getLocalStorageService().get<string>(STORAGE_KEYS.AUTH_TOKEN);
		}
	}

	private async refreshExpiredToken(): Promise<string | null> {
		try {
			const { getTokenManager } = await import("@/infrastructure/di/services");
			return await getTokenManager().forceRefresh();
		} catch (error) {
			getLogger().error("ApiClient: Token refresh failed:", error);
			return null;
		}
	}

	private isAuthEndpoint(endpoint: string): boolean {
		// Only these specific auth endpoints don't need tokens
		const noTokenEndpoints = ["/auth/login", "/auth/refresh", "/auth/logout"];
		return noTokenEndpoints.some((noTokenEndpoint) =>
			endpoint.startsWith(noTokenEndpoint),
		);
	}

	public resetSessionState(): void {
		this.sessionExpiredHandled = false;
	}

	private async handleRevokedRefreshToken(): Promise<void> {
		if (this.sessionExpiredHandled) return;
		this.sessionExpiredHandled = true;

		// Clear all auth-related data from storage
		const localStore = getLocalStorageService();
		localStore.remove(STORAGE_KEYS.AUTH_TOKEN);
		localStore.remove(STORAGE_KEYS.AUTH_REFRESH_TOKEN);
		localStore.remove(STORAGE_KEYS.USER_DATA);

		// Clear session storage too
		getSessionStorageService().clear();

		// Clear Sentry user context on session expiry
		getMonitoring().clearUserContext();

		// Notify AuthContext via typed notifier
		getSessionExpiredNotifier().notify(
			"Your session has expired. Please log in again.",
		);
	}
}

export const apiClient = new ApiClient();
